<template>
  <el-form
    ref="formRef"
    :model="formData"
    :rules="rules"
    label-width="100px"
    class="company-form"
  >
    <el-form-item label="公司名称" prop="name">
      <el-input v-model="formData.name" placeholder="请输入公司名称" clearable />
    </el-form-item>

    <el-form-item label="公司类型" prop="companyType">
      <el-select v-model="formData.companyType" placeholder="请选择公司类型" class="full-width" clearable>
        <el-option label="大厂" value="大厂" />
        <el-option label="外企" value="外企" />
        <el-option label="国企" value="国企" />
        <el-option label="创业公司" value="创业公司" />
        <el-option label="其他" value="其他" />
      </el-select>
    </el-form-item>

    <el-form-item label="所属行业" prop="industry">
      <el-input v-model="formData.industry" placeholder="请输入所属行业" clearable />
    </el-form-item>

    <el-form-item label="所在地" prop="location">
      <el-input v-model="formData.location" placeholder="请输入公司所在地" clearable />
    </el-form-item>

    <el-form-item label="招聘官网" prop="recruitmentUrl">
      <el-input 
        v-model="formData.recruitmentUrl" 
        placeholder="请输入招聘官网地址"
        clearable
      >
        <template #append>
          <el-button v-if="formData.recruitmentUrl" @click="openUrl(formData.recruitmentUrl)">
            <el-icon><Link /></el-icon>
          </el-button>
        </template>
      </el-input>
    </el-form-item>

    <el-form-item label="联系人" prop="contactPerson">
      <el-input v-model="formData.contactPerson" placeholder="请输入联系人姓名" clearable />
    </el-form-item>

    <el-form-item label="备注" prop="notes">
      <el-input
        v-model="formData.notes"
        type="textarea"
        :rows="3"
        placeholder="请输入备注信息"
      />
    </el-form-item>

    <el-form-item label="状态" prop="status">
      <el-select v-model="formData.status" placeholder="请选择状态" class="full-width" clearable>
        <el-option label="计划投递" value="planned" />
        <el-option label="已投递" value="applied" />
        <el-option label="面试中" value="interviewing" />
        <el-option label="已录用" value="offered" />
        <el-option label="已拒绝" value="rejected" />
      </el-select>
    </el-form-item>

    <el-form-item>
      <el-space>
        <el-checkbox v-model="formData.isStarred">收藏公司</el-checkbox>
        <el-checkbox v-model="formData.isPinned">置顶显示</el-checkbox>
      </el-space>
    </el-form-item>
  </el-form>
</template>

<script lang="ts" setup>
import { ref, reactive } from 'vue';
import type { FormInstance, FormRules } from 'element-plus';
import type { Company } from '@/types/company';
import { Link } from '@element-plus/icons-vue';

const props = defineProps<{
  initialData?: Partial<Company>;
  loading?: boolean;
}>();

const emit = defineEmits<{
  (e: 'submit', data: Company): void;
}>();

const formRef = ref<FormInstance>();
const formData = reactive({
  name: props.initialData?.name || '',
  companyType: props.initialData?.companyType || '',
  industry: props.initialData?.industry || '',
  location: props.initialData?.location || '',
  recruitmentUrl: props.initialData?.recruitmentUrl || '',
  contactPerson: props.initialData?.contactPerson || '',
  notes: props.initialData?.notes || '',
  status: props.initialData?.status || 'planned',
  isStarred: props.initialData?.isStarred || false,
  isPinned: props.initialData?.isPinned || false,
});

const rules: FormRules = {
  name: [
    { required: true, message: '公司名称不能为空', trigger: 'blur' },
    { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
  ],
  companyType: [
    { required: true, message: '公司类型不能为空', trigger: 'change' }
  ],
  recruitmentUrl: [
    { required: true, message: '招聘网站链接不能为空', trigger: 'blur' },
    { 
      pattern: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/, 
      message: '请输入有效的URL', 
      trigger: 'blur' 
    }
  ],
  status: [
    { required: true, message: '请选择状态', trigger: 'change' }
  ]
};

// 提交表单
const submitForm = async () => {
  if (!formRef.value) return;
  
  await formRef.value.validate((valid, fields) => {
    if (valid) {
      emit('submit', {
        ...formData,
        id: props.initialData?.id,
        recruitmentUrl: formData.recruitmentUrl ? 
          (formData.recruitmentUrl.startsWith('http') ? formData.recruitmentUrl : `https://${formData.recruitmentUrl}`)
          : '',
        createTime: props.initialData?.createTime
      });
    }
  });
};

// 打开URL链接
const openUrl = (url: string) => {
  let finalUrl = url;
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    finalUrl = 'https://' + url;
  }
  window.open(finalUrl, '_blank');
};

// 暴露方法给父组件
defineExpose({
  submitForm
});
</script>

<style scoped>
.company-form {
  padding: 20px 0;
}

.full-width {
  width: 100%;
}

:deep(.el-select) {
  width: 100%;
}

:deep(.el-form-item__content) {
  flex-wrap: nowrap;
}

:deep(.el-input-group__append) {
  padding: 0;
  overflow: hidden;
}

:deep(.el-input-group__append button) {
  border: none;
  margin: 0;
  height: 100%;
  border-radius: 0;
}
</style> 